import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, firstValueFrom } from 'rxjs';
import { collapsedMenu } from 'src/app/STORE/selectors/menu.selector';
import * as routerActions from 'src/app/STORE/actions/router.actions';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { HttpBaseService } from 'src/app/base/http-base.service';
import { ApiConstants } from 'src/app/base/api-constants';
import { error } from 'console';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { getAllKeywords, getKeywordsMeta } from 'src/app/STORE/selectors/util.selector';
import { setKeywords, setKeywordsMeta } from 'src/app/STORE/actions/util.actions';
import { userData } from 'src/app/STORE/selectors/auth.selector';
import { AppStateService } from 'src/app/services/app-state.service';
import { HeaderService } from 'src/app/services/header.service';

@Component({
  selector: 'vb-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  standalone: false //NOSONAR
})
export class HomeComponent implements OnInit {
  private store = inject(Store);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private titleService = inject(Title);
  private translateService = inject(TranslateService);
  private appGlobalService = inject(AppGlobalService);
  private http = inject(HttpClient);
  private destroyRef = inject(DestroyRef);
  httpBaseService = inject(HttpBaseService);
  private readonly appStateService = inject(AppStateService);
  private readonly headerService = inject(HeaderService);

  title = 'eva-design-studio';
  collapsedMenu$: Observable<any> = this.store.select(collapsedMenu);
  isOuterRoute = false;
  isAppManagementRoute = false;
  isBuildRoute = false;
  hasAppId = false;
  isPreviewRoute = false;

  isBetaAccessPrivilegeConsumable = false;
  isBetaAccessPrivilegeConsumableForUser = false;

  /**
   * Gates the body behind a loader until the vibe flags are conclusively
   * resolved, so the user never sees a flash of the wrong shell before the
   * flags settle. Revealed on the first userData resolution (before first paint).
   */
  isContentReady = false;

  private isComponentInitialized: boolean = false;

  constructor() {
    this.listenRouterChange();
    // Keep the build-shell styling (no top border/radius on .app-main__inner)
    // in lock-step with when the build nav-tab-bar is shown.
    this.headerService.activeArtifactTab$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((tab) => {
        this.isBuildRoute = tab === 'build';
      });
  }

  listenRouterChange() {
    this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        this.updateRouteData();

        this.updateIsBetaAccessPrivilegeConsumable();
      }
    });
  }

  updateRouteData() {
    let route = this.route.firstChild;
    while (route?.firstChild) {
      route = route.firstChild;
    }

    const routeData = route?.snapshot.data as { pageTitle?: string; label?: string } | undefined;

    const curPath = this.route.snapshot.firstChild?.routeConfig?.path;

    const appId = this.route.snapshot.paramMap.get('appId') || this.route.parent?.snapshot.paramMap.get('appId');
    this.hasAppId = !!appId;

    this.isOuterRoute = curPath === 'login';
    this.isAppManagementRoute = curPath === 'appmanagement' || curPath === 'settings' || curPath === 'dashboards';

    this.isPreviewRoute = curPath === 'preview';

    this.setDocumentTitle(routeData);

    route?.data?.subscribe((data) => {
      this.store.dispatch(routerActions.setNavigationEnd({ routerEvent: data }));
      this.store.dispatch(routerActions.setCurNavigation({ curPage: curPath }));
      this.store.dispatch(routerActions.setCurPage({ currentPage: route!.snapshot.data }));
    });
  }

  handleNavigationEnd(event) {
    this.updateRouteData();
  }

  setDocumentTitle(routeData?: { pageTitle?: string; label?: string }) {
    if (this.isAppManagementRoute) {
      const titleKey = routeData?.pageTitle || routeData?.label;
      const pageTitle = titleKey ? this.translateService.instant(titleKey) : 'Applications';
      this.titleService.setTitle(pageTitle);
    }
  }

  getMessageBundle(apiName, storageName) {
    this.httpBaseService.get(apiName).subscribe({
      next: (response: any) => {
        this.appGlobalService.write(storageName, response);
      },
      error: () => console.info('error occurred')
    });
  }

  async getReservedKeywords(apiName, storageName) {
    const allKeywords = await firstValueFrom(this.store.select(getAllKeywords));
    const meta = await firstValueFrom(this.store.select(getKeywordsMeta));
    const cachedReservedKeywords = this.appGlobalService.getFromSessionStorage(storageName);
    const cachedMeta = this.appGlobalService.getFromSessionStorage(`${storageName}_meta`);

    let etag = '';
    let cacheTime = 0;
    let maxAge = 0;

    if (meta) {
      etag = meta.etag;
      cacheTime = meta.cacheTime ? new Date(meta.cacheTime).getTime() : 0;
      maxAge = meta.maxAge;
    }

    const now = Date.now();
    const hasKeywords =
      allKeywords &&
      (allKeywords.restrictedKeywords?.length > 0 ||
        allKeywords.restrictedSqlKeywords?.length > 0 ||
        allKeywords.restrictedTableKeywords?.length > 0);

    if (hasKeywords && maxAge && now - cacheTime < maxAge * 1000) {
      // Use cached data
      return;
    }

    this.http
      .get(`/${environment.apihost}/${apiName.url}`, {
        headers: etag ? { 'If-None-Match': etag } : {},
        observe: 'response'
      })
      .subscribe({
        next: (response: any) => {
          const etagHeader = response.headers?.get('ETag') ? response.headers?.get('ETag').replace(/"/g, '') : '';
          const cacheControl = response.headers?.get('Cache-Control');
          let maxAgeValue = 0;
          if (cacheControl) {
            const match = cacheControl.match(/max-age=(\d+)/);
            if (match) maxAgeValue = parseInt(match[1], 10);
          }

          const body = response.body;
          if (body) {
            this.store.dispatch(
              setKeywords({
                restrictedKeywords: body.RESTRICTED_KEYWORDS || [],
                restrictedSqlKeywords: body.RESTRICTED_SQL_KEYWORDS || [],
                restrictedTableKeywords: body.RESTRICTED_TABLE_KEYWORDS || []
              })
            );
          }

          this.store.dispatch(
            setKeywordsMeta({
              keywordsMeta: {
                etag: etagHeader,
                cacheTime: new Date(now),
                maxAge: maxAgeValue
              }
            })
          );
        },
        error: (err) => {
          if (err.status === 304 && cachedReservedKeywords) {
            // Not Modified, use cached data
            console.info('Using cached reserved keywords');
          } else {
            console.info('error occurred while fetching reserved keywords', err);
          }
        }
      });
  }

  ngOnInit() {
    console.log('HomeComponent - ngOnInit()');

    this.configureListeners();
  }

  configureListeners() {
    this.store
      .select(userData)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((userInfo: any) => {
        if (userInfo) {
          // Pre-write the user-level vibe flags from the authenticated user so the shell
          // resolves to the correct mode on this subscription's first synchronous emission
          // (before first paint), instead of relying on the header's later getUser$ effect.
          if (userInfo.email) {
            this.appGlobalService.write('isBetaAccessEnabled', userInfo.betaAccess || false);
            this.appGlobalService.write('raiModeEnabledForUser', userInfo.raiModeEnabled || false);

            // Flags are now resolved for a real user — reveal the body and drop the loader.
            setTimeout(() => {
            this.isContentReady = true;
            },500);
          }

          this.updateIsBetaAccessPrivilegeConsumable();

          if (!this.isComponentInitialized) {
            this.isComponentInitialized = true;

            this.updateRouteData();

            this.getMessageBundle(ApiConstants.getReferenceMessageBundle, 'referenceMessageBundle');
            this.getMessageBundle(ApiConstants.getPrevalidationMessageBundle, 'prevalidationMessageBundle');
            this.getReservedKeywords(ApiConstants.getReservedKeywords, 'reservedKeywords');

            this.setShowConsoleErrorPanelFlag();
          }
        }
      });

    this.listenToRefreshAppInfo();
  }

  private listenToRefreshAppInfo() {
    this.appStateService.DataObservables.refreshAppInfo.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (paramsMap: any) => {
        if (!paramsMap.isEmpty) {
          this.updateIsBetaAccessPrivilegeConsumable();
        }
      }
    });
  }

  updateIsBetaAccessPrivilegeConsumable() {
    this.isBetaAccessPrivilegeConsumable = this.appGlobalService.isBetaAccessPrivilegeConsumableForApp();
    this.isBetaAccessPrivilegeConsumableForUser = this.appGlobalService.isBetaAccessPrivilegeConsumableForUser();
  }

  setShowConsoleErrorPanelFlag() {
    const key = 'showConsoleErrorPannel';
    const currentenv = this.appGlobalService?.get('environmentInfo')?.envInfo?.env;
    const isDevorTest = currentenv === 'DEV' || currentenv === 'ACC';

    if (localStorage?.getItem(key) === null) {
      if (isDevorTest) {
        localStorage?.setItem(key, 'true');
      } else {
        localStorage?.setItem(key, 'false');
      }
    }
  }
}
